import { useEffect, useRef } from "react";
import { sendReaderEvent, newOpId } from "@/lib/readerEvents";

/** Playback-attempt model for one clip inside the reels/video player (single source for the admin «الفيديو» tab).
 *  One ATTEMPT = the visitor arrives on the clip (active). Within the attempt:
 *   - video_attempt  → sent once (op_id = attempt id)
 *   - video_play     → first real frame = ONE successful start (server dedupes per session × clip; a quality switch, a
 *                      fallback source or a resume after waiting is the SAME attempt and never a new start)
 *   - video_fail     → final MediaError (no more sources), video_abandon → visitor left before the first frame
 *   - video_watch    → summary (max-merged server-side): watched_sec (timeupdate deltas while playing — pauses, buffering and
 *                      seeks add nothing), coverage_pct, ttff_ms, data stalls (buffer ran dry) separated from manual pauses and
 *                      seeks, quality switches, completed.
 *  `meta` is a mutable ref shared with the player: { quality, switches, failed }. Listeners are re-attached when the <video>
 *  element is re-created (`nonce`) while the attempt state lives on, so stats survive a quality switch. */
export function useWatchMetrics(ref, reel, enabled = true, nonce = 0, meta) {
    const st = useRef(null);
    useEffect(() => {
        if (!enabled || !reel?.id) return;
        const base = { articleId: reel.id, slug: reel.slug };
        const attempt = newOpId().slice(0, 20);
        const s = { attempt, watched: 0, lastT: null, ttff: null, startAt: performance.now(), stalls: 0, stallMs: 0, stallSince: null, played: false, pauses: 0, seeks: 0, seekUntil: 0, completed: false, closed: false, sent: false, failed: false, startQuality: meta?.current?.quality || null };
        st.current = s;
        if (meta) { meta.current.switches = 0; meta.current.failed = false; }
        // Sent on the next tick: React StrictMode (dev) mounts/unmounts once immediately — that must not produce a fake attempt.
        const h = setTimeout(() => { s.sent = true; sendReaderEvent("video_attempt", { ...base, opId: attempt, target: attempt }); }, 250);
        return () => {
            clearTimeout(h);
            const m = st.current; if (!m || m.closed) return;
            m.closed = true;
            if (!m.sent) return;
            flush(ref.current, m, true, base, meta);
            if (!m.played && !m.failed) sendReaderEvent("video_abandon", { ...base, opId: `${m.attempt}-ab`, target: m.attempt });
        };
    }, [reel?.id, enabled]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const v = ref.current, s = st.current;
        if (!v || !enabled || !s) return;
        const base = { articleId: reel.id, slug: reel.slug };
        const onPlaying = () => {
            if (s.stallSince !== null) { s.stallMs += performance.now() - s.stallSince; s.stallSince = null; }
            if (!s.played) { s.played = true; s.ttff = Math.round(performance.now() - s.startAt); sendReaderEvent("video_play", { ...base, opId: `${s.attempt}-play`, target: s.attempt }); }
            s.lastT = v.currentTime;
        };
        // «waiting» right after a seek or while seeking is the visitor moving inside the clip, NOT a data stall.
        const onWaiting = () => { s.lastT = null; if (!s.played || v.seeking || performance.now() < s.seekUntil || v.paused) return; if (s.stallSince === null) { s.stalls += 1; s.stallSince = performance.now(); } };
        const onTime = () => {
            if (v.paused || v.seeking) { s.lastT = v.currentTime; return; }
            if (s.lastT !== null) { const d = v.currentTime - s.lastT; if (d > 0 && d < 1.5) s.watched += d; }
            s.lastT = v.currentTime;
        };
        const onSeeking = () => { s.lastT = null; s.seeks += 1; s.seekUntil = performance.now() + 1500; if (s.stallSince !== null) { s.stallMs += performance.now() - s.stallSince; s.stallSince = null; } };
        const onPause = () => { s.lastT = null; if (s.played && !v.ended && !v.seeking && !v.error && document.visibilityState === "visible") s.pauses += 1; flush(v, s, false, base, meta); };
        const onEnded = () => { s.lastT = null; s.completed = true; flush(v, s, true, base, meta); };
        const onHide = () => { if (document.visibilityState === "hidden") flush(v, s, false, base, meta); };
        v.addEventListener("playing", onPlaying); v.addEventListener("waiting", onWaiting); v.addEventListener("timeupdate", onTime);
        v.addEventListener("seeking", onSeeking); v.addEventListener("pause", onPause); v.addEventListener("ended", onEnded);
        document.addEventListener("visibilitychange", onHide); window.addEventListener("pagehide", onHide);
        return () => {
            flush(v, s, false, base, meta);
            v.removeEventListener("playing", onPlaying); v.removeEventListener("waiting", onWaiting); v.removeEventListener("timeupdate", onTime);
            v.removeEventListener("seeking", onSeeking); v.removeEventListener("pause", onPause); v.removeEventListener("ended", onEnded);
            document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", onHide);
        };
    }, [ref, reel?.id, reel?.slug, enabled, nonce]);   // eslint-disable-line react-hooks/exhaustive-deps

    return { fail: () => { const s = st.current; if (!s || s.failed) return; s.failed = true; if (meta) meta.current.failed = true; sendReaderEvent("video_fail", { articleId: reel.id, slug: reel.slug, opId: `${s.attempt}-fail`, target: s.attempt }); } };
}

function coverage(v) {
    try {
        const d = v?.duration; if (!d || !isFinite(d)) return null;
        let s = 0; for (let i = 0; i < v.played.length; i++) s += v.played.end(i) - v.played.start(i);
        return Math.min(100, Math.round((s / d) * 100));
    } catch { return null; }
}

function flush(v, s, final, base, meta) {
    if (!s.played) return;
    if (s.stallSince !== null && final) { s.stallMs += performance.now() - s.stallSince; s.stallSince = null; }
    sendReaderEvent("video_watch", { ...base, target: s.attempt, metrics: {
        attempt_id: s.attempt, watched_sec: Math.round(s.watched), coverage_pct: coverage(v), ttff_ms: s.ttff, stalls: s.stalls, stall_ms: Math.round(s.stallMs),
        manual_pauses: s.pauses, seeks: s.seeks, quality_switches: meta?.current?.switches || 0, start_quality: s.startQuality, end_quality: meta?.current?.quality || null,
        duration_sec: v && isFinite(v.duration) ? Math.round(v.duration) : null, completed: s.completed, final: !!final } });
}
